import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home } from '@moderepo/mode-internal-apis';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showSuccessNotification }
    from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { fetchHomeById, userAppDataContext, selectHomeById, clearUserHomes, clearProjectHomes, removeHomeMember, deleteHome, clearEntities }
    from '@moderepo/mode-data-state-user-app';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useRitzTranslation } from '../../utils/customHooks';
import { HomeInfoContainer } from './HomeInfoContainer';
import { RouteHelper } from '../routes/RouteHelper';
import { useSelectUser } from '../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface HomeInfoPreviewContainerProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number | undefined;
}


/**
 * This Component is used for displaying HomeInfo on the preview panel for preview. It does not show all home details e.g. devices, logs,
 * kv pairs, etc... but only the Home object details
 */
export const HomeInfoPreviewContainer = (props: HomeInfoPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const user = useSelectUser(props.projectId, props.userId);
    const { trans } = useRitzTranslation(project);
    const isNewHome = props.homeId === undefined;
    const routeHelper = RouteHelper.getInstance(navigate);
    const selectedHome = props.homeId ? selectHomeById(appDataState, props.homeId) : undefined;
    const classes = useStyle();



    /**
     * This useEffect used for loading home data on component init
     */
    useEffect(() => {
        if (props.homeId && !selectedHome) {
            appDataDispatch(fetchHomeById(props.homeId)).catch(() => { /* ignore error */ });
        }
    }, [props.userId, props.homeId, selectedHome, appDataDispatch]);


    /**
     * Unselect the selected home and update the URL
     */
    const unselectHome = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * Take the user to the home details page
     */
    const viewHomeDetails = useCallback((homeId: number) => {
        routeHelper.gotoHomeDetailsPage({ projectId, userId: props.userId, homeId });
        uiDispatch(hideSlideOutPanel());
    }, [routeHelper, projectId, props.userId, uiDispatch]);


    /**
     * On create new home canceled event from HomeInfoContainer we need to unselected the selected entity to close the preview panel
     */
    const onNewHomeCanceled = useCallback(() => {
        unselectHome();
    }, [unselectHome]);



    /**
     * On create new home success, we will take the user to the new created home details page
     */
    const onNewHomeCreated = useCallback(() => {
        unselectHome();
    }, [unselectHome]);



    /**
     * use useMemo to create the the newHomeSettings so that this doesn't get created on every re-render
     */
    const newHomeSettings = useMemo(() => {
        return {
            onNewHomeCreated,
            onNewHomeCanceled,
        };
    }, [onNewHomeCreated, onNewHomeCanceled]);


    const onRemoveHomeActionClicked = useCallback((home: Home, errorMessage?: string | undefined) => {
        // Confirm before removing home
        uiDispatch(showDialog({
            type       : DialogType.INPUT,
            title      : props.userId ? 'dialog.confirm_remove_member.title' : 'dialog.confirm_delete_home.title',
            message    : props.userId ? 'dialog.confirm_remove_member.message' : 'dialog.confirm_delete_home.message',
            messageData: {
                member_name: user?.name || user?.id || props.userId,
                home_name  : home.name,
            },
            positiveButton: props.userId ? 'label.remove' : 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : capitalizeAll(trans('label.name')),
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'terms.home',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const userResponse = response.input ? response.input.trim() : undefined;

                    if (!userResponse) {
                        // User input is not provided
                        onRemoveHomeActionClicked(home, 'error.input.generic.empty');
                    } else if (userResponse !== home.name) {
                        // user input does not match homeName
                        onRemoveHomeActionClicked(home, 'error.input.generic.invalid');
                    } else {
                        const action = props.userId
                            ? removeHomeMember(home.id, props.userId)       // Remove the user from a home but the Home still exist
                            : deleteHome(home.id);                          // Delete the home which remove all home members as well.

                        appDataDispatch(action).then(async () => {

                            if (props.userId) {
                                // Clear the userHomes data in the data state because the current list of User homes are no longer up to date
                                await appDataDispatch(clearUserHomes(props.userId));
                            } else {
                                // Clear the projectHomes data in the data state because the current list of Project homes are no longer up to date
                                await appDataDispatch(clearProjectHomes(props.projectId));

                                // Clear all the entities we have in cache because some of them might belong to the home we have just deleted.
                                await appDataDispatch(clearEntities(props.projectId));
                            }

                            uiDispatch(showSuccessNotification('notif.generic_item_removed_successfully', {
                                term: capitalizeAll(trans('terms.home')),
                            }));

                            unselectHome();
                        }).catch(() => {});
                    }

                }
            },
        }));
    }, [uiDispatch, props.userId, props.projectId, user?.name, user?.id, appDataDispatch, trans, unselectHome]);



    const primaryActions = useMemo(() => {
        if (selectedHome) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_home_details')),
                onClick: () => {
                    viewHomeDetails(props.homeId as number);
                },
            }];
            return actions;
        }

        return undefined;
    }, [props.homeId, selectedHome, trans, viewHomeDetails]);


    const secondaryActions = useMemo(() => {
        if (selectedHome) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans(props.userId ? 'label.leave_home' : 'label.delete_home')),
                onClick  : () => {
                    onRemoveHomeActionClicked(selectedHome);
                },
            }];
            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, onRemoveHomeActionClicked, props.userId, selectedHome, trans]);



    const slideOutCompContent = useMemo(() => {
        return (
            <HomeInfoContainer
                className="transparent"
                infoCompProps={{
                    formProps: {
                        className: 'single-column',
                    },
                }}
                projectId={projectId}
                userId={props.userId}
                homeId={props.homeId}
                newHomeSettings={
                    isNewHome ? newHomeSettings : undefined
                }
            />
        );
    }, [isNewHome, newHomeSettings, projectId, props.homeId, props.userId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewHome}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
